import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import PanelBreadcrumbs from './PanelBreadcrumbs';
import Toast from './Toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const INITIAL_PRODUCT_FORM = {
  merchantId: '',
  merchantLabel: '',
  name: '',
  description: '',
  price: '',
  stock: '',
  categoryId: '',
  categoryLabel: '',
  imagesText: '',
  status: 'active',
};

const Products = ({ setActivePage }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(12);
  const [status, setStatus] = useState('');
  const [merchantId, setMerchantId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [search, setSearch] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Category search states (autocomplete)
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [categorySearchResults, setCategorySearchResults] = useState([]);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isSearchingCategories, setIsSearchingCategories] = useState(false);
  const categorySearchTimeoutRef = useRef(null);
  const categoryDropdownRef = useRef(null);

  // Merchant search states (autocomplete)
  const [merchantSearchQuery, setMerchantSearchQuery] = useState('');
  const [merchantSearchResults, setMerchantSearchResults] = useState([]);
  const [isMerchantDropdownOpen, setIsMerchantDropdownOpen] = useState(false);
  const [isSearchingMerchants, setIsSearchingMerchants] = useState(false);
  const merchantSearchTimeoutRef = useRef(null);
  const merchantDropdownRef = useRef(null);

  // Add product modal states
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [editingProductMeta, setEditingProductMeta] = useState(null);
  const [deleteTargetProduct, setDeleteTargetProduct] = useState(null);
  const [isDeletingProduct, setIsDeletingProduct] = useState(false);
  const [productForm, setProductForm] = useState(INITIAL_PRODUCT_FORM);
  const [isSubmittingProduct, setIsSubmittingProduct] = useState(false);
  const [toast, setToast] = useState(null);
  const [addCategoryResults, setAddCategoryResults] = useState([]);
  const [addMerchantResults, setAddMerchantResults] = useState([]);
  const [isAddCategoryDropdownOpen, setIsAddCategoryDropdownOpen] = useState(false);
  const [isAddMerchantDropdownOpen, setIsAddMerchantDropdownOpen] = useState(false);
  const [isAddCategorySearching, setIsAddCategorySearching] = useState(false);
  const [isAddMerchantSearching, setIsAddMerchantSearching] = useState(false);
  const addCategoryTimeoutRef = useRef(null);
  const addMerchantTimeoutRef = useRef(null);
  const addCategoryDropdownRef = useRef(null);
  const addMerchantDropdownRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProducts();
    }, search ? 500 : 0); // Debounce search

    return () => clearTimeout(timer);
  }, [page, limit, status, merchantId, categoryId, search]);

  // Close category dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsCategoryDropdownOpen(false);
      }
      if (merchantDropdownRef.current && !merchantDropdownRef.current.contains(event.target)) {
        setIsMerchantDropdownOpen(false);
      }
      if (addCategoryDropdownRef.current && !addCategoryDropdownRef.current.contains(event.target)) {
        setIsAddCategoryDropdownOpen(false);
      }
      if (addMerchantDropdownRef.current && !addMerchantDropdownRef.current.contains(event.target)) {
        setIsAddMerchantDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const searchCategoriesByQuery = async (query) => {
    const authData = localStorage.getItem('superadmin_auth');
    const token = authData ? JSON.parse(authData).token : null;
    const normalizedQuery = query?.trim() || '';
    const shouldUseSearch = normalizedQuery.length >= 2;
    const response = await axios.get(`${API_BASE_URL}/api/superadmin/categories`, {
      params: {
        page: 1,
        limit: 10,
        ...(shouldUseSearch ? { search: normalizedQuery } : {}),
      },
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const categories = response.data?.data || response.data?.categories || [];
    return Array.isArray(categories) ? categories : [];
  };

  const searchMerchantsByQuery = async (query) => {
    const authData = localStorage.getItem('superadmin_auth');
    const token = authData ? JSON.parse(authData).token : null;
    const normalizedQuery = query?.trim() || '';
    const shouldUseSearch = normalizedQuery.length >= 2;
    const response = await axios.get(
      shouldUseSearch ? `${API_BASE_URL}/api/merchants/search` : `${API_BASE_URL}/api/superadmin/merchants`,
      {
        params: shouldUseSearch
          ? {
              q: normalizedQuery,
              limit: 10,
            }
          : {
              page: 1,
              limit: 10,
            },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    const merchants = response.data?.data || response.data?.merchants || [];
    return Array.isArray(merchants) ? merchants : [];
  };

  // Search categories API call
  const searchCategories = async (query) => {
    try {
      setIsSearchingCategories(true);
      const categories = await searchCategoriesByQuery(query);
      setCategorySearchResults(categories);
    } catch (err) {
      // Don't show error if session expired (401) - user will be redirected to login
      if (err.isSessionExpired || (err.response && err.response.status === 401)) {
        return;
      }
      console.error('Error searching categories:', err);
      setCategorySearchResults([]);
    } finally {
      setIsSearchingCategories(false);
    }
  };

  // Debounced category search
  useEffect(() => {
    if (categorySearchTimeoutRef.current) {
      clearTimeout(categorySearchTimeoutRef.current);
    }

    if (categorySearchQuery.trim().length >= 2 || isCategoryDropdownOpen) {
      categorySearchTimeoutRef.current = setTimeout(() => {
        searchCategories(categorySearchQuery);
      }, 300);
    } else {
      setCategorySearchResults([]);
    }

    return () => {
      if (categorySearchTimeoutRef.current) {
        clearTimeout(categorySearchTimeoutRef.current);
      }
    };
  }, [categorySearchQuery, isCategoryDropdownOpen]);

  // Search merchants API call
  const searchMerchants = async (query) => {
    try {
      setIsSearchingMerchants(true);
      const merchants = await searchMerchantsByQuery(query);
      setMerchantSearchResults(merchants);
    } catch (err) {
      // Don't show error if session expired (401) - user will be redirected to login
      if (err.isSessionExpired || (err.response && err.response.status === 401)) {
        return;
      }
      console.error('Error searching merchants:', err);
      setMerchantSearchResults([]);
    } finally {
      setIsSearchingMerchants(false);
    }
  };

  // Debounced merchant search
  useEffect(() => {
    if (merchantSearchTimeoutRef.current) {
      clearTimeout(merchantSearchTimeoutRef.current);
    }

    if (merchantSearchQuery.trim().length >= 2 || isMerchantDropdownOpen) {
      merchantSearchTimeoutRef.current = setTimeout(() => {
        searchMerchants(merchantSearchQuery);
      }, 300);
    } else {
      setMerchantSearchResults([]);
    }

    return () => {
      if (merchantSearchTimeoutRef.current) {
        clearTimeout(merchantSearchTimeoutRef.current);
      }
    };
  }, [merchantSearchQuery, isMerchantDropdownOpen]);

  // Debounced merchant search for add-product modal
  useEffect(() => {
    if (!showAddProductModal) return;

    if (addMerchantTimeoutRef.current) {
      clearTimeout(addMerchantTimeoutRef.current);
    }

    if (productForm.merchantLabel.trim().length >= 2 || isAddMerchantDropdownOpen) {
      addMerchantTimeoutRef.current = setTimeout(async () => {
        try {
          setIsAddMerchantSearching(true);
          const merchants = await searchMerchantsByQuery(productForm.merchantLabel);
          setAddMerchantResults(merchants);
        } catch (err) {
          if (err.isSessionExpired || (err.response && err.response.status === 401)) {
            return;
          }
          setAddMerchantResults([]);
        } finally {
          setIsAddMerchantSearching(false);
        }
      }, 300);
    } else {
      setAddMerchantResults([]);
    }

    return () => {
      if (addMerchantTimeoutRef.current) {
        clearTimeout(addMerchantTimeoutRef.current);
      }
    };
  }, [productForm.merchantLabel, showAddProductModal, isAddMerchantDropdownOpen]);

  // Debounced category search for add-product modal
  useEffect(() => {
    if (!showAddProductModal) return;

    if (addCategoryTimeoutRef.current) {
      clearTimeout(addCategoryTimeoutRef.current);
    }

    if (productForm.categoryLabel.trim().length >= 2 || isAddCategoryDropdownOpen) {
      addCategoryTimeoutRef.current = setTimeout(async () => {
        try {
          setIsAddCategorySearching(true);
          const categories = await searchCategoriesByQuery(productForm.categoryLabel);
          setAddCategoryResults(categories);
        } catch (err) {
          if (err.isSessionExpired || (err.response && err.response.status === 401)) {
            return;
          }
          setAddCategoryResults([]);
        } finally {
          setIsAddCategorySearching(false);
        }
      }, 300);
    } else {
      setAddCategoryResults([]);
    }

    return () => {
      if (addCategoryTimeoutRef.current) {
        clearTimeout(addCategoryTimeoutRef.current);
      }
    };
  }, [productForm.categoryLabel, showAddProductModal, isAddCategoryDropdownOpen]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get auth token from localStorage
      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;
      
      // Build query parameters
      const params = {
        page,
        limit,
      };
      
      if (status) params.status = status;
      if (merchantId) params.merchant_id = parseInt(merchantId);
      if (categoryId) params.category_id = parseInt(categoryId);
      if (search) params.search = search;
      
      const response = await axios.get(`${API_BASE_URL}/api/superadmin/products`, {
        params,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Handle response - adjust based on actual API response structure
      const data = response.data;
      
      if (data.products && Array.isArray(data.products)) {
        setProducts(data.products);
      } else if (Array.isArray(data)) {
        setProducts(data);
      } else if (data.data && Array.isArray(data.data)) {
        setProducts(data.data);
      }
      
      // Handle pagination metadata - check multiple possible formats
      if (data.pagination) {
        setTotalPages(data.pagination.total_pages || data.pagination.totalPages || 1);
        setTotalItems(data.pagination.total || data.pagination.total_items || data.pagination.totalItems || 0);
      } else {
        if (data.total_pages !== undefined) {
          setTotalPages(data.total_pages);
        } else if (data.totalPages !== undefined) {
          setTotalPages(data.totalPages);
        } else if (data.total !== undefined && limit) {
          setTotalPages(Math.ceil(data.total / limit));
        } else {
          setTotalPages(1);
        }
        
        if (data.total !== undefined) {
          setTotalItems(data.total);
        } else if (data.total_items !== undefined) {
          setTotalItems(data.total_items);
        } else if (data.totalItems !== undefined) {
          setTotalItems(data.totalItems);
        } else {
          // If no total provided, estimate from current page data
          const currentItems = data.products?.length || data.data?.length || (Array.isArray(data) ? data.length : 0);
          setTotalItems(currentItems > 0 ? currentItems : 0);
        }
      }
    } catch (err) {
      // Don't show error if session expired (401) - user will be redirected to login
      if (err.isSessionExpired || (err.response && err.response.status === 401)) {
        return;
      }
      console.error('Error fetching products:', err);
      setError(err.response?.data?.error || 'Failed to load products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (typeof amount === 'number') {
      return '$' + amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return amount || '$0.00';
  };

  const handleFilterChange = (filterType, value) => {
    // Reset to page 1 when filters change
    setPage(1);
    if (filterType === 'status') setStatus(value);
    if (filterType === 'merchant_id') setMerchantId(value);
    if (filterType === 'category_id') setCategoryId(value);
    if (filterType === 'search') setSearch(value);
  };

  const handleCategorySelect = (category) => {
    setCategoryId(category?.id ?? '');
    setCategorySearchQuery(category?.name || '');
    setIsCategoryDropdownOpen(false);
    setPage(1);
  };

  const handleCategorySearchChange = (value) => {
    setCategorySearchQuery(value);
    setIsCategoryDropdownOpen(true);
    if (!value) {
      setCategoryId('');
      setPage(1);
    }
  };

  const handleMerchantSelect = (merchant) => {
    setMerchantId(merchant.id || merchant.merchant_id || '');
    setMerchantSearchQuery(merchant.shop_name || merchant.name || merchant.merchant_name || merchant.email || '');
    setIsMerchantDropdownOpen(false);
    setPage(1);
  };

  const handleMerchantSearchChange = (value) => {
    setMerchantSearchQuery(value);
    setIsMerchantDropdownOpen(true);
    if (!value) {
      setMerchantId('');
      setPage(1);
    }
  };

  const clearFilters = () => {
    setPage(1);
    setStatus('');
    setMerchantId('');
    setCategoryId('');
    setSearch('');
    setCategorySearchQuery('');
    setCategorySearchResults([]);
    setIsCategoryDropdownOpen(false);
    setMerchantSearchQuery('');
    setMerchantSearchResults([]);
    setIsMerchantDropdownOpen(false);
  };

  const closeAddProductModal = () => {
    setShowAddProductModal(false);
    setEditingProductMeta(null);
    setProductForm(INITIAL_PRODUCT_FORM);
    setAddCategoryResults([]);
    setAddMerchantResults([]);
    setIsAddCategoryDropdownOpen(false);
    setIsAddMerchantDropdownOpen(false);
  };

  const handleAddProductFieldChange = (field, value) => {
    setProductForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddProductMerchantInput = (value) => {
    setProductForm((prev) => ({
      ...prev,
      merchantLabel: value,
      merchantId: '',
    }));
    setIsAddMerchantDropdownOpen(true);
  };

  const handleAddProductCategoryInput = (value) => {
    setProductForm((prev) => ({
      ...prev,
      categoryLabel: value,
      categoryId: '',
    }));
    setIsAddCategoryDropdownOpen(true);
  };

  const handleAddProductMerchantSelect = (merchant) => {
    const selectedId = merchant?.id || merchant?.merchant_id || '';
    const selectedLabel = merchant?.shop_name || merchant?.name || merchant?.merchant_name || merchant?.email || '';
    setProductForm((prev) => ({
      ...prev,
      merchantId: String(selectedId),
      merchantLabel: selectedLabel,
    }));
    setIsAddMerchantDropdownOpen(false);
  };

  const handleAddProductCategorySelect = (category) => {
    const selectedId = category?.id || category?.category_id || '';
    const selectedLabel = category?.name || category?.category_name || '';
    setProductForm((prev) => ({
      ...prev,
      categoryId: String(selectedId),
      categoryLabel: selectedLabel,
    }));
    setIsAddCategoryDropdownOpen(false);
  };

  const parseImages = (imagesText) => {
    if (!imagesText.trim()) return [];
    return imagesText
      .split(/\r?\n|,/)
      .map((img) => img.trim())
      .filter(Boolean);
  };

  const getProductId = (product) => product?.id || product?.product_id || null;

  const getProductMerchantId = (product) =>
    product?.merchant_id ||
    product?.merchantId ||
    product?.merchant?.id ||
    product?.merchant?.merchant_id ||
    product?.merchant?.user_id ||
    product?.seller_id ||
    product?.store_id ||
    product?.merchant ||
    null;

  const getProductCategoryId = (product) =>
    product?.category_id || product?.categoryId || product?.category?.id || product?.category?.category_id || null;

  const getProductMerchantLabel = (product) =>
    product?.merchant_name ||
    product?.merchant_shop_name ||
    product?.merchant?.shop_name ||
    product?.merchant?.name ||
    '';

  const getProductCategoryLabel = (product) =>
    product?.category_name || product?.category?.name || product?.category?.category_name || '';

  const getProductImagesText = (product) => {
    if (Array.isArray(product?.images)) return product.images.join('\n');
    if (typeof product?.images === 'string') return product.images;
    if (typeof product?.image_url === 'string') return product.image_url;
    return '';
  };

  const openAddProductModal = () => {
    setEditingProductMeta(null);
    setProductForm(INITIAL_PRODUCT_FORM);
    setShowAddProductModal(true);
  };

  const openEditProductModal = (product) => {
    const productIdValue = getProductId(product);
    const merchantIdValue = getProductMerchantId(product);

    if (!productIdValue || !merchantIdValue) {
      setToast({ message: 'Unable to edit this product: missing product or merchant ID.', type: 'error' });
      return;
    }

    const priceValue = product?.price ?? product?.product_price ?? '';
    const stockValue = product?.stock ?? product?.stock_quantity ?? 0;
    const normalizedStatus = String(product?.status || 'active').toLowerCase() === 'inactive' ? 'inactive' : 'active';
    const categoryIdValue = getProductCategoryId(product);

    setEditingProductMeta({
      productId: String(productIdValue),
    });

    setProductForm({
      merchantId: String(merchantIdValue),
      merchantLabel: getProductMerchantLabel(product),
      name: product?.name || product?.product_name || '',
      description: product?.description || '',
      price: priceValue !== '' && priceValue !== null && priceValue !== undefined ? String(priceValue) : '',
      stock: stockValue !== null && stockValue !== undefined ? String(stockValue) : '0',
      categoryId: categoryIdValue ? String(categoryIdValue) : '',
      categoryLabel: getProductCategoryLabel(product),
      imagesText: getProductImagesText(product),
      status: normalizedStatus,
    });

    setShowAddProductModal(true);
  };

  const openDeleteProductModal = (product) => {
    const productIdValue = getProductId(product);
    const merchantIdValue = getProductMerchantId(product);

    if (!productIdValue || !merchantIdValue) {
      setToast({ message: 'Unable to delete this product: missing product or merchant ID.', type: 'error' });
      return;
    }

    setDeleteTargetProduct({
      productId: String(productIdValue),
      merchantId: String(merchantIdValue),
      name: product?.name || product?.product_name || 'this product',
    });
  };

  const closeDeleteProductModal = () => {
    if (isDeletingProduct) return;
    setDeleteTargetProduct(null);
  };

  const handleDeleteProduct = async () => {
    if (!deleteTargetProduct) return;

    const parsedProductId = parseInt(deleteTargetProduct.productId, 10);
    const parsedMerchantId = parseInt(deleteTargetProduct.merchantId, 10);

    if (!Number.isInteger(parsedProductId) || !Number.isInteger(parsedMerchantId)) {
      setToast({ message: 'Invalid product selected for deletion.', type: 'error' });
      return;
    }

    try {
      setIsDeletingProduct(true);
      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;

      await axios.delete(`${API_BASE_URL}/api/merchants/${parsedMerchantId}/products/${parsedProductId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setToast({ message: 'Product deleted successfully.', type: 'success' });
      setDeleteTargetProduct(null);
      fetchProducts();
    } catch (err) {
      if (err.isSessionExpired || (err.response && err.response.status === 401)) {
        return;
      }
      const apiError = err.response?.data?.error || err.response?.data?.message || 'Failed to delete product';
      setToast({ message: apiError, type: 'error' });
    } finally {
      setIsDeletingProduct(false);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();

    const parsedMerchantId = parseInt(productForm.merchantId, 10);
    const parsedCategoryId = parseInt(productForm.categoryId, 10);
    const parsedPrice = Number(productForm.price);
    const parsedStock = parseInt(productForm.stock, 10);

    if (!Number.isInteger(parsedMerchantId) || parsedMerchantId <= 0) {
      setToast({ message: 'Please select a valid merchant.', type: 'error' });
      return;
    }

    if (!productForm.name.trim()) {
      setToast({ message: 'Product name is required.', type: 'error' });
      return;
    }

    if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
      setToast({ message: 'Please enter a valid price.', type: 'error' });
      return;
    }

    if (!Number.isInteger(parsedStock) || parsedStock < 0) {
      setToast({ message: 'Please enter a valid stock value.', type: 'error' });
      return;
    }

    if (!Number.isInteger(parsedCategoryId) || parsedCategoryId <= 0) {
      setToast({ message: 'Please select a valid category.', type: 'error' });
      return;
    }

    try {
      setIsSubmittingProduct(true);

      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;

      const payload = {
        name: productForm.name.trim(),
        description: productForm.description.trim(),
        price: parsedPrice,
        stock: parsedStock,
        category_id: parsedCategoryId,
        images: parseImages(productForm.imagesText),
        status: productForm.status || 'active',
      };

      if (editingProductMeta?.productId) {
        const parsedProductId = parseInt(editingProductMeta.productId, 10);
        if (!Number.isInteger(parsedProductId) || parsedProductId <= 0) {
          setToast({ message: 'Invalid product selected for update.', type: 'error' });
          return;
        }

        await axios.patch(`${API_BASE_URL}/api/merchants/${parsedMerchantId}/products/${parsedProductId}`, payload, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      } else {
        await axios.post(`${API_BASE_URL}/api/merchants/${parsedMerchantId}/products`, payload, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }

      setToast({
        message: editingProductMeta ? 'Product updated successfully.' : 'Product created successfully.',
        type: 'success'
      });
      closeAddProductModal();
      setPage(1);
      fetchProducts();
    } catch (err) {
      if (err.isSessionExpired || (err.response && err.response.status === 401)) {
        return;
      }
      const apiError = err.response?.data?.error || err.response?.data?.message || (editingProductMeta ? 'Failed to update product' : 'Failed to create product');
      setToast({ message: apiError, type: 'error' });
    } finally {
      setIsSubmittingProduct(false);
    }
  };

  if (loading && products.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
            <p className="text-gray-600">Loading products...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="p-6">
      <PanelBreadcrumbs current="Products" setActivePage={setActivePage} />
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Products</h1>
          <p className="text-gray-600 mt-1">Manage your product catalog</p>
        </div>
        <button
          onClick={openAddProductModal}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium"
        >
          + Add Product
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Search in product name or description"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          
          <div className="relative" ref={merchantDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">Merchant</label>
            <div className="relative">
              <input
                type="text"
                value={merchantSearchQuery}
                onChange={(e) => handleMerchantSearchChange(e.target.value)}
                onFocus={() => setIsMerchantDropdownOpen(true)}
                placeholder="Search merchant by shop name, name or email"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {isSearchingMerchants && (
                <div className="absolute right-3 top-2.5">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                </div>
              )}
            </div>

            {/* Merchant Dropdown */}
            {isMerchantDropdownOpen && merchantSearchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                {merchantSearchResults.map((merchant, index) => (
                  <div
                    key={merchant.id || merchant.merchant_id || index}
                    onClick={() => handleMerchantSelect(merchant)}
                    className="px-4 py-2 hover:bg-indigo-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <div className="font-medium text-gray-900">
                      {merchant.shop_name || merchant.name || merchant.merchant_name || 'Unknown'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {merchant.email || merchant.merchant_email || ''}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {isMerchantDropdownOpen && merchantSearchQuery.trim().length >= 2 && !isSearchingMerchants && merchantSearchResults.length === 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                <div className="px-4 py-2 text-sm text-gray-500 text-center">
                  No merchants found
                </div>
              </div>
            )}
          </div>
          
          <div className="relative" ref={categoryDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <div className="relative">
              <input
                type="text"
                value={categorySearchQuery}
                onChange={(e) => handleCategorySearchChange(e.target.value)}
                onFocus={() => setIsCategoryDropdownOpen(true)}
                placeholder="Search category by name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {isSearchingCategories && (
                <div className="absolute right-3 top-2.5">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                </div>
              )}
            </div>

            {/* Category Dropdown */}
            {isCategoryDropdownOpen && categorySearchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                {categorySearchResults.map((category, index) => (
                  <div
                    key={category.id || index}
                    onClick={() => handleCategorySelect(category)}
                    className="px-4 py-2 hover:bg-indigo-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <div className="font-medium text-gray-900">
                      {category.name || 'Unknown'}
                    </div>
                    {(category.parent_name || category.product_count !== undefined) && (
                      <div className="text-sm text-gray-500">
                        {category.parent_name ? `Parent: ${category.parent_name}` : ''}
                        {category.parent_name && category.product_count !== undefined ? ' • ' : ''}
                        {category.product_count !== undefined ? `${category.product_count} products` : ''}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {isCategoryDropdownOpen && categorySearchQuery.trim().length >= 2 && !isSearchingCategories && categorySearchResults.length === 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                <div className="px-4 py-2 text-sm text-gray-500 text-center">
                  No categories found
                </div>
              </div>
            )}
          </div>
          
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-700">{error}</span>
            <button
              onClick={fetchProducts}
              className="ml-auto px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Products Grid */}
      {products.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <p className="text-gray-500 text-lg">No products found</p>
          {search && (
            <p className="text-gray-400 text-sm mt-2">Try adjusting your search or filters</p>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-6">
            {products.map((product, index) => (
              <div key={product.id || product.product_id || index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {product.name || product.product_name || '-'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {product.category_name || product.category?.name || product.category || '-'}
                  </p>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Price:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(product.price || product.product_price)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Stock:</span>
                    <span className={`text-sm font-medium ${
                      (product.stock || product.stock_quantity || 0) > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {product.stock || product.stock_quantity || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Status:</span>
                    <span className={`text-sm font-medium ${
                      (product.status === 'active' || product.status === 'Active') ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {product.status ? (product.status.charAt(0).toUpperCase() + product.status.slice(1)) : '-'}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => openEditProductModal(product)}
                    className="flex-1 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 px-3 py-2 rounded-lg text-sm font-medium"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => openDeleteProductModal(product)}
                    className="flex-1 bg-red-50 text-red-600 hover:bg-red-100 px-3 py-2 rounded-lg text-sm font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-6 py-4 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing page {page} of {totalPages || 1} {totalItems > 0 && `(${totalItems} total products)`}
              {products.length > 0 && ` - Showing ${products.length} items`}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-sm text-gray-700">
                Page {page} / {totalPages || 1}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages || 1, p + 1))}
                disabled={page >= (totalPages || 1) || loading}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>

          {/* Items per page selector */}
          <div className="mt-4 flex justify-end">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Items per page:</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(parseInt(e.target.value));
                  setPage(1);
                }}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="12">12</option>
                <option value="24">24</option>
                <option value="48">48</option>
                <option value="96">96</option>
              </select>
            </div>
          </div>
        </>
      )}
        {showAddProductModal && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingProductMeta ? 'Edit Product' : 'Add Product'}
                </h2>
                <button
                  onClick={closeAddProductModal}
                  disabled={isSubmittingProduct}
                  className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
                  aria-label="Close add product modal"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCreateProduct} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative" ref={addMerchantDropdownRef}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Merchant *</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={productForm.merchantLabel}
                        onChange={(e) => handleAddProductMerchantInput(e.target.value)}
                        onFocus={() => setIsAddMerchantDropdownOpen(true)}
                        placeholder="Search merchant by shop name or email"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      {isAddMerchantSearching && (
                        <div className="absolute right-3 top-2.5">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                        </div>
                      )}
                    </div>
                    {isAddMerchantDropdownOpen && addMerchantResults.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                        {addMerchantResults.map((merchant, index) => (
                          <div
                            key={merchant.id || merchant.merchant_id || index}
                            onClick={() => handleAddProductMerchantSelect(merchant)}
                            className="px-4 py-2 hover:bg-indigo-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">
                              {merchant.shop_name || merchant.name || merchant.merchant_name || 'Unknown'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {merchant.email || merchant.merchant_email || ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="relative" ref={addCategoryDropdownRef}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={productForm.categoryLabel}
                        onChange={(e) => handleAddProductCategoryInput(e.target.value)}
                        onFocus={() => setIsAddCategoryDropdownOpen(true)}
                        placeholder="Search category by name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      {isAddCategorySearching && (
                        <div className="absolute right-3 top-2.5">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                        </div>
                      )}
                    </div>
                    {isAddCategoryDropdownOpen && addCategoryResults.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                        {addCategoryResults.map((category, index) => (
                          <div
                            key={category.id || category.category_id || index}
                            onClick={() => handleAddProductCategorySelect(category)}
                            className="px-4 py-2 hover:bg-indigo-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">
                              {category.name || category.category_name || 'Unknown'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Product Name *</label>
                    <input
                      type="text"
                      value={productForm.name}
                      onChange={(e) => handleAddProductFieldChange('name', e.target.value)}
                      placeholder="Enter product name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status *</label>
                    <select
                      value={productForm.status}
                      onChange={(e) => handleAddProductFieldChange('status', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Price *</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={productForm.price}
                      onChange={(e) => handleAddProductFieldChange('price', e.target.value)}
                      placeholder="0.00"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Stock *</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={productForm.stock}
                      onChange={(e) => handleAddProductFieldChange('stock', e.target.value)}
                      placeholder="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    rows={3}
                    value={productForm.description}
                    onChange={(e) => handleAddProductFieldChange('description', e.target.value)}
                    placeholder="Enter product description"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Images (optional)</label>
                  <textarea
                    rows={3}
                    value={productForm.imagesText}
                    onChange={(e) => handleAddProductFieldChange('imagesText', e.target.value)}
                    placeholder="Paste image URLs separated by comma or new line"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={closeAddProductModal}
                    disabled={isSubmittingProduct}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingProduct}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium disabled:opacity-50"
                  >
                    {isSubmittingProduct ? (editingProductMeta ? 'Updating...' : 'Creating...') : (editingProductMeta ? 'Update Product' : 'Create Product')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {deleteTargetProduct && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Delete Product</h2>
              </div>
              <div className="px-6 py-5">
                <p className="text-sm text-gray-700">
                  Are you sure you want to delete <span className="font-semibold text-gray-900">{deleteTargetProduct.name}</span>?
                </p>
                <p className="mt-2 text-xs text-gray-500">
                  This action performs a soft delete and cannot be easily undone from this panel.
                </p>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeDeleteProductModal}
                  disabled={isDeletingProduct}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteProduct}
                  disabled={isDeletingProduct}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium disabled:opacity-50"
                >
                  {isDeletingProduct ? 'Deleting...' : 'Delete Product'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Products;
